import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { receiptApi } from 'src/services/receipts/api'
import { receiptQueries } from 'src/services/receipts/queries'
import { Button } from 'src/components/button'
import { Modal } from 'src/components/modal'
import { Spinner } from 'src/components/spinner'
import { useToast } from 'src/components/toast'
import { extractErrorMessage } from 'src/utils/errors'

interface ReceiptNotesModalProps {
  receiptId: string
  isOpen: boolean
  onClose: () => void
}

export const ReceiptNotesModal: React.FC<ReceiptNotesModalProps> = ({
  receiptId,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [notes, setNotes] = useState('')

  // Linked receipts in a stack only carry id/name/asset, so read the notes fresh.
  const { data: receipt, isLoading } = useQuery({
    ...receiptQueries.retrieve(receiptId),
    enabled: isOpen && Boolean(receiptId),
  })

  useEffect(() => {
    if (isOpen && receipt) setNotes(receipt.notes ?? '')
  }, [isOpen, receipt])

  const { mutate: saveNotes, isPending: isSaving } = useMutation({
    // Blank is valid: generation simply runs without standing notes.
    mutationFn: () => receiptApi.update({ id: receiptId, notes: notes.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receiptQueries.all() })
      onClose()
    },
    onError: (error) => {
      toast({
        description: extractErrorMessage(error, t('receipts.notesSaveError')),
        type: 'error',
      })
    },
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('receipts.notes')}>
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">{t('receipts.notesHelp')}</p>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('receipts.notesPlaceholder')}
            className="w-full rounded-md border border-border-strong p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            rows={4}
            data-testid="receipt-notes-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => saveNotes()}
              disabled={isSaving}
              data-testid="receipt-notes-save"
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
